/**
 * A staff link opens the client that owns it (#10718 slice 4).
 *
 * Client success reviews users' chats and records by clicking links to them.
 * When a staff member's home tab loads such a link and the page's read is
 * refused (403) or finds nothing (404), the app asks the server, once, to
 * open the client that owns the resource (`POST /staff/grants/for-path`). The
 * server opens a grant there, taking admin access only when member access
 * would not show the resource, and answers with a one-time code; the tab then
 * enters the client at the same path, and the page simply appears to load.
 *
 * This module holds the stateless half: which paths name a resource (a
 * mirror of the server's table in `fp/staff_access_links.py`), the refusal
 * signal `fpFetch` sends, and the per-tab memory of paths the server said
 * name nothing to open. The asking itself is `StaffLinkEntry`, mounted with
 * the app shell, which owns the once-per-page guard.
 *
 * Spec: .claude/features/10718-staff-access-architecture/slice-4-links-open-the-client.md
 */
import { BASE_API_URL_V2 } from "@/constants";
import { isClientEnvironment } from "./clientEnvironment";

export const FOR_PATH_URL = `${BASE_API_URL_V2}/api/v2/staff/grants/for-path`;

/** Sent on `window` when a home tab's API read is refused or finds nothing. */
export const RESOURCE_REFUSED_EVENT = "fp:resource-refused";

/** Tell the app shell that a read of this page was refused. Called by
 * `fpFetch` for every failed response; only a home tab's GET answered 403 or
 * 404 is passed on. Never from a client tab: there the staff member is
 * already the client's collaborator, and a refusal is the client's answer. */
export function announceRefusal(status: number, method: string): void {
  if (status !== 403 && status !== 404) return;
  if (method.toUpperCase() !== "GET") return;
  if (isClientEnvironment()) return;
  window.dispatchEvent(new Event(RESOURCE_REFUSED_EVENT));
}

// The app's resource routes, mirroring `_ROUTES` on the server. Each names
// the pattern of a page that shows one resource, and, for a page whose
// resource rides in its query string, the parameter that carries it.
const SEGMENT = "[^/]+";
const RMS_PAGES = new Set([
  "components",
  "home",
  "view",
  "settings",
  "interactions",
  "interaction",
]);
const WORKSPACE_PAGES = new Set(["new", "chat"]);

type Route = {
  pattern: RegExp;
  names: (match: RegExpMatchArray, query: URLSearchParams) => boolean;
};

const always = (): boolean => true;

const ROUTES: readonly Route[] = [
  { pattern: new RegExp(`^/chat/${SEGMENT}$`), names: always },
  { pattern: new RegExp(`^/notetaker/chat/${SEGMENT}$`), names: always },
  {
    pattern: new RegExp(`^/agents/${SEGMENT}/sessions/${SEGMENT}$`),
    names: always,
  },
  {
    pattern: new RegExp(
      `^/agents/${SEGMENT}/runs/${SEGMENT}/conversations/${SEGMENT}$`
    ),
    names: always,
  },
  { pattern: new RegExp(`^/agents/${SEGMENT}(?:/.*)?$`), names: always },
  {
    pattern: /^\/documents$/,
    names: (_, query) =>
      Boolean(query.get("docId") || query.get("interactionId")),
  },
  {
    pattern: /^\/tools\/fp-highlighter$/,
    names: (_, query) => Boolean(query.get("fileId")),
  },
  { pattern: new RegExp(`^/rms/interaction/${SEGMENT}$`), names: always },
  { pattern: new RegExp(`^/notetaker/meeting/${SEGMENT}$`), names: always },
  { pattern: new RegExp(`^/rms/view/${SEGMENT}$`), names: always },
  {
    pattern: new RegExp(`^/rms/(${SEGMENT})/${SEGMENT}(?:/.*)?$`),
    names: (match) => !RMS_PAGES.has(match[1]),
  },
  {
    pattern: new RegExp(
      `^/(?:reports|matrix|matrix-templates|artifacts)/(${SEGMENT})$`
    ),
    names: (match) => !WORKSPACE_PAGES.has(match[1]),
  },
];

/** Whether `path` (pathname and query) is a page that shows one resource. */
export function namesResource(path: string): boolean {
  let url: URL;
  try {
    url = new URL(path, "https://same-origin.invalid");
  } catch {
    return false;
  }
  const pathname = url.pathname.replace(/\/+$/, "") || "/";
  for (const route of ROUTES) {
    const match = pathname.match(route.pattern);
    if (match) return route.names(match, url.searchParams);
  }
  return false;
}

// Paths the server said name nothing to open (404) or name the staff
// member's own organization (409), for the rest of this tab's session: the
// page shows its normal error and the path is never asked about again.
const NOTHING_TO_OPEN_KEY = "fp_staff_link_nothing_to_open";

function nothingToOpen(): string[] {
  try {
    const raw = sessionStorage.getItem(NOTHING_TO_OPEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is string => typeof entry === "string")
      : [];
  } catch {
    return [];
  }
}

/** Whether this tab already learned that `path` opens nothing. */
export function knownToOpenNothing(path: string): boolean {
  return nothingToOpen().includes(path);
}

/** Remember, for this tab's session, that `path` opens nothing. */
export function rememberOpensNothing(path: string): void {
  try {
    const known = nothingToOpen();
    if (!known.includes(path)) known.push(path);
    sessionStorage.setItem(NOTHING_TO_OPEN_KEY, JSON.stringify(known));
  } catch {
    // Blocked storage: the next load of the page may ask once more.
  }
}
